import { X } from "lucide-react";
import { type ReactNode, useEffect } from "react";
import type { EvidenceEntry } from "./types";

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  rawText: string;
  evidence: EvidenceEntry[];
  score: number | null;
  posterCompany?: string | null;
  posterName?: string | null;
}

// Build a non-overlapping list of (start, end, phrase, weight) spans from the
// evidence array, clipped to the text bounds. Offsets of -1 (poster-company
// evidence) are surfaced as separate badges above the text, not inline.
function buildSpans(text: string, evidence: EvidenceEntry[]) {
  return evidence
    .filter((e) => e.offset >= 0)
    .map((e) => ({
      start: e.offset,
      end: Math.min(e.offset + e.phrase.length, text.length),
      phrase: e.phrase,
      weight: e.weight,
    }))
    .filter((s) => s.start < s.end)
    .sort((a, b) => a.start - b.start)
    .reduce<Array<{ start: number; end: number; phrase: string; weight: number }>>((acc, span) => {
      const last = acc[acc.length - 1];
      if (!last || span.start >= last.end) acc.push(span);
      return acc;
    }, []);
}

function HighlightedText({
  text,
  evidence,
}: {
  text: string;
  evidence: EvidenceEntry[];
}): ReactNode {
  const spans = buildSpans(text, evidence);
  if (spans.length === 0) {
    return <pre className="whitespace-pre-wrap text-sm text-text-secondary">{text}</pre>;
  }

  const nodes: ReactNode[] = [];
  let cursor = 0;
  spans.forEach((span) => {
    if (span.start > cursor) {
      nodes.push(text.slice(cursor, span.start));
    }
    nodes.push(
      <mark
        key={`evidence-${span.start}-${span.end}`}
        className="rounded bg-amber-200 px-1 text-amber-900 dark:bg-amber-400/30 dark:text-amber-100"
        title={`weight ${span.weight}`}
      >
        {text.slice(span.start, span.end)}
      </mark>,
    );
    cursor = span.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));

  return <pre className="whitespace-pre-wrap text-sm text-text-secondary">{nodes}</pre>;
}

export function JobPostingEvidenceModal({
  open,
  onClose,
  title,
  rawText,
  evidence,
  score,
  posterCompany,
  posterName,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;
  const posterEvidence = evidence.filter((e) => e.offset < 0);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="evidence-modal-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40"
    >
      <button
        type="button"
        aria-label="Close evidence modal"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-lg bg-card shadow-xl ring-1 ring-ring">
        <div className="flex items-start justify-between border-b border-border px-5 py-3">
          <div>
            <h2 id="evidence-modal-title" className="text-base font-semibold text-text-primary">
              {title}
            </h2>
            <p className="mt-0.5 text-xs text-text-muted">
              Agency likelihood score: {score ?? "—"} / 100
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-text-muted hover:bg-card-hover"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          {(posterName || posterCompany || posterEvidence.length > 0) && (
            <div className="mb-4 flex flex-wrap items-center gap-2">
              {posterName && (
                <span className="rounded-full bg-card-alt px-2 py-0.5 text-xs text-text-secondary">
                  Posted by: {posterName}
                </span>
              )}
              {posterCompany && (
                <span className="rounded-full bg-card-alt px-2 py-0.5 text-xs text-text-secondary">
                  Poster company: {posterCompany}
                </span>
              )}
              {posterEvidence.map((e) => (
                <span
                  key={`poster-evidence-${e.phrase}`}
                  className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-900 dark:bg-amber-400/30 dark:text-amber-100"
                  title={`weight ${e.weight}`}
                >
                  {e.phrase}
                </span>
              ))}
            </div>
          )}
          <HighlightedText text={rawText || "(no raw text)"} evidence={evidence} />
        </div>
      </div>
    </div>
  );
}
